import React, { useState } from 'react';
import { Card, CardContent } from "@shared/ui/card";
import { ValueLabel } from "@shared/components/ValueLabel";
import { User, Mail, Phone, MapPin, Tag, Calendar, Shield, Edit2, Users, Building, FileText } from "lucide-react";
import { Button } from "@shared/ui/button";
import { EditPersonalInfo } from "./EditPersonalInfo";

import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useConsumerDetail } from "../../../hooks";
import { useTranslation } from "react-i18next";

export const PersonalInfoLanding = () => {
  const { t } = useTranslation();
  const { remoteUtilityId, remoteConsumerNumber } = getLoginDataFromStorage();
  const [isEditing, setIsEditing] = useState(false);

  const { data: response, isLoading } = useConsumerDetail({
    remote_utility_id: Number(remoteUtilityId),
    consumer_no: remoteConsumerNumber,
  });

  const consumerData = response?.result;
  const profileType = consumerData?.ownershipDisplay || t("Not Provided");
  
  const formatAddress = (addressObj: any) => {
    if (!addressObj) return t("Not Provided");
    return addressObj.address || addressObj.ADDRESS || t("Not Provided");
  };

  const hasCoApplicant = consumerData?.additionalData?.secondaryConsumer && 
                         Object.keys(consumerData.additionalData.secondaryConsumer).length > 0;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column: Profile Summary */}
      <Card className="w-full lg:w-[320px] shrink-0 border border-slate-100 shadow-sm rounded-xl h-fit">
        <CardContent className="flex flex-col items-center text-center">
          <div className="w-[84px] h-[84px] bg-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold tracking-wide mb-4">
            {consumerData?.firstName?.[0] || ''}{consumerData?.lastName?.[0] || ''}
          </div>
          <h2 className="text-[17px] font-bold text-slate-900 mb-1">{consumerData?.firstName} {consumerData?.lastName}</h2>
          <p className="text-sm text-slate-500 mb-4">{profileType}</p>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 mb-8">
            {consumerData?.statusDisplay || t("N/A")}
          </span>
          
          <div className="flex flex-col gap-2.5 text-sm w-full">

            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-500">{t("Account No:")}</span>
              <span className="font-semibold text-slate-900">{consumerData?.consumerNo || t("N/A")}</span>
            </div>
            <div className="flex justify-between items-center gap-4">
              <span className="text-slate-500">{t("Since:")}</span>
              <span className="font-semibold text-slate-900">
                {consumerData?.registrationDate 
                  ? new Date(consumerData.registrationDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : t("N/A")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Right Column: Details */}
      {isEditing ? (
        <EditPersonalInfo consumerData={consumerData} onCancel={() => setIsEditing(false)} onSave={() => setIsEditing(false)} />
      ) : (
        <Card className="flex-1 border border-slate-100 shadow-sm rounded-xl overflow-hidden h-fit">
          <div className="">
          <div className="flex items-center justify-between mb-8">
            <h3 className="text-[17px] font-bold text-slate-900">{t("Personal Information")}</h3>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="h-[34px] px-4 rounded-lg flex items-center gap-1.5 text-sm font-medium"
                onClick={() => setIsEditing(true)}
              >
                <Edit2 className="w-3.5 h-3.5" /> {t("Edit")}
              </Button>
            </div>
          </div>

          {/* Section 1: Personal Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-7 gap-x-12 mb-10">
            {[
              {
                label: t("First Name"),
                value: consumerData?.firstName || t("Not Provided"),
                icon: <User className="w-4 h-4 text-slate-500" />
              },
              {
                label: t("Last Name"),
                value: consumerData?.lastName || t("Not Provided"),
                icon: <User className="w-4 h-4 text-slate-500" />
              },
              {
                label: t("Email Address"),
                value: consumerData?.email || t("Not Provided"),
                icon: <Mail className="w-4 h-4 text-slate-500" />
              },
              {
                label: t("Phone Number"),
                value: consumerData?.contactNumber || t("Not Provided"),
                icon: <Phone className="w-4 h-4 text-slate-500" />
              },
              {
                label: t("Service Address"),
                value: formatAddress(consumerData?.addressData?.service),
                icon: <MapPin className="w-4 h-4 text-slate-500" />,
                className: "md:col-span-2"
              },
              {
                label: t("Mailing Address"),
                value: formatAddress(consumerData?.addressData?.billing),
                icon: <MapPin className="w-4 h-4 text-slate-500" />,
                className: "md:col-span-2"
              }
            ].map((item, idx) => (
              <ValueLabel
                key={idx}
                icon={
                  <div className="flex items-center justify-center w-[30px] h-[30px] bg-slate-100 rounded-lg">
                    {item.icon}
                  </div>
                }
                label={item.label}
                value={item.value}
                className={item.className}
              />
            ))}
          </div>

          <hr className="border-slate-100 my-8" />

          {/* Section 2: Additional Details */}
          <h4 className="text-[13px] font-bold text-slate-600 tracking-wide mb-6">{t("Additional Details")}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-7 gap-x-12 mb-10">
            {[
              {
                label: t("Category"),
                value: consumerData?.categoryDisplay || t("Not Provided"),
                icon: <Tag className="w-4 h-4 text-slate-500" />
              },
              {
                label: t("Sub Category"),
                value: consumerData?.subCategoryDisplay || t("Not Provided"),
                icon: <Tag className="w-4 h-4 text-slate-500" />
              },
              {
                label: t("SSN / Tax ID"),
                value: consumerData?.ssn || t("Not Provided"),
                icon: <Shield className="w-4 h-4 text-slate-500" />
              },
              {
                label: t("Date of Birth"),
                value: consumerData?.dateOfBirth || t("Not Provided"),
                icon: <Calendar className="w-4 h-4 text-slate-500" />
              }
            ].map((item, idx) => (
              <ValueLabel
                key={idx}
                icon={
                  <div className="flex items-center justify-center w-[30px] h-[30px] bg-slate-100 rounded-lg">
                    {item.icon}
                  </div>
                }
                label={item.label}
                value={item.value}
              />
            ))}
          </div>

          <hr className="border-slate-100 my-8" />
          {hasCoApplicant && (
            <>
              <hr className="border-slate-100 my-8" />
              {/* Section 3: Co-Applicant Details */}
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-[18px] h-[18px] text-slate-400" />
                <h4 className="text-[13px] font-bold text-slate-600 tracking-wide">{t("Co-Applicant Details")}</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-7 gap-x-12">
                {[
                  {
                    label: t("Name"),
                    value: `${consumerData.additionalData.secondaryConsumer.firstName || ''} ${consumerData.additionalData.secondaryConsumer.lastName || ''}`,
                    icon: <User className="w-4 h-4 text-blue-500" />
                  },
                  {
                    label: t("Email"),
                    value: consumerData.additionalData.secondaryConsumer.email || t("Not Provided"),
                    icon: <Mail className="w-4 h-4 text-blue-500" />
                  },
                  {
                    label: t("Phone"),
                    value: consumerData.additionalData.secondaryConsumer.contactNumber || t("Not Provided"),
                    icon: <Phone className="w-4 h-4 text-blue-500" />
                  },
                  {
                    label: t("Relation"),
                    value: consumerData.additionalData.secondaryConsumer.relation || t("Not Provided"),
                    icon: <Users className="w-4 h-4 text-blue-500" />
                  }
                ].map((item, idx) => (
                  <ValueLabel
                    key={idx}
                    icon={
                      <div className="flex items-center justify-center w-[30px] h-[30px] bg-blue-50 rounded-lg">
                        {item.icon}
                      </div>
                    }
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </div>
            </>
          )}

          {/* Section 4: Conditional Blocks based on Status */}
          {profileType === 'Sub-Account' && (
            <div className="mt-10 bg-[#f8fafe] border border-indigo-100/50 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <Building className="w-[18px] h-[18px] text-indigo-400" />
                <h4 className="text-[13px] font-bold text-slate-700 tracking-wide">{t("Master Account Details")}</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-7 gap-x-12">
                {[
                  {
                    label: t("Name"),
                    value: "Samoa Water Authority - HQ",
                    icon: <User className="w-4 h-4 text-indigo-500" />
                  },
                  {
                    label: t("Phone"),
                    value: "+685 800-1000",
                    icon: <Phone className="w-4 h-4 text-indigo-500" />
                  },
                  {
                    label: t("Email"),
                    value: "accounts@samoawater.ws",
                    icon: <Mail className="w-4 h-4 text-indigo-500" />
                  },
                  {
                    label: t("Service Address"),
                    value: "1 Government Road, Apia, Upolu",
                    icon: <MapPin className="w-4 h-4 text-indigo-500" />
                  }
                ].map((item, idx) => (
                  <ValueLabel
                    key={idx}
                    icon={
                      <div className="flex items-center justify-center w-[30px] h-[30px] bg-indigo-100/50 rounded-lg">
                        {item.icon}
                      </div>
                    }
                    label={item.label}
                    value={item.value}
                  />
                ))}
              </div>
            </div>
          )}

          {profileType === 'Tenant' && (
            <div className="mt-10 bg-[#fffcf5] border border-amber-100/60 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <User className="w-[18px] h-[18px] text-amber-500" />
                <h4 className="text-[13px] font-bold text-slate-700 tracking-wide">{t("Owner Details")}</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-y-7 gap-x-12">
                {[
                  {
                    label: t("Name"),
                    value: "Robert Miller",
                    icon: <User className="w-4 h-4 text-amber-600" />
                  },
                  {
                    label: t("Email"),
                    value: "r.miller@email.com",
                    icon: <Mail className="w-4 h-4 text-amber-600" />
                  },
                  {
                    label: t("Phone"),
                    value: "+685 555-0142",
                    icon: <Phone className="w-4 h-4 text-amber-600" />
                  },
                  {
                    label: t("Owner Type"),
                    value: "Property Owner",
                    icon: <FileText className="w-4 h-4 text-amber-600" />
                  },
                  {
                    label: t("Address"),
                    value: "12 Main Street, Apia, Upolu",
                    icon: <MapPin className="w-4 h-4 text-amber-600" />,
                    className: "md:col-span-2"
                  }
                ].map((item, idx) => (
                  <ValueLabel
                    key={idx}
                    icon={
                      <div className="flex items-center justify-center w-[30px] h-[30px] bg-amber-100/50 rounded-lg">
                        {item.icon}
                      </div>
                    }
                    label={item.label}
                    value={item.value}
                    className={item.className}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
      )}
    </div>
  );
};
